import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Calendar, ArrowLeft, Share2, Linkedin, Facebook, Twitter } from 'lucide-react';
import { blogs } from '../data/mock';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const blog = blogs.find(b => b.slug === slug);

  if (!blog) {
    return (
      <div className="pt-24 min-h-screen bg-[#FAFFEE]">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold text-[#004534] mb-4">Blog Not Found</h1>
          <p className="text-[#807979] mb-8">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 bg-[#004534] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#0C6951] transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-[#FAFFEE] to-[#EDEDFE] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#D3FF62]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -left-40 w-96 h-96 bg-[#CACAFC]/30 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[#0C6951] font-semibold uppercase tracking-wider text-sm mb-4">
              <Link to="/" className="hover:text-[#004534]">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/blogs" className="hover:text-[#004534]">Blogs</Link>
              <ChevronRight className="w-4 h-4" />
              Article
            </span>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#004534] mb-6 leading-tight">
              {blog.title}
            </h1>
            
            <div className="flex items-center gap-4 text-[#807979]">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{blog.date}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Image */}
      {blog.image && (
        <section className="bg-white py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <img
                src={blog.image}
                alt={blog.title}
                className="w-full h-[400px] object-cover rounded-3xl shadow-lg"
              />
            </div>
          </div>
        </section>
      )}

      {/* Blog Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <article className="prose prose-lg max-w-none">
              {blog.content ? (
                <div dangerouslySetInnerHTML={{ __html: blog.content }} />
              ) : (
                <div className="space-y-6 text-[#004534]">
                  <p className="text-xl leading-relaxed text-[#0C6951]">
                    {blog.excerpt}
                  </p>
                  
                  <div className="bg-[#FAFFEE] rounded-2xl p-8 my-8">
                    <p className="text-[#807979] italic">
                      This article is part of MLDC Agro's Knowledge Center, where we share insights and analysis to help farmers and businesses make informed decisions in the agricultural sector.
                    </p>
                  </div>
                  
                  <p className="text-[#807979] leading-relaxed">
                    At MLDC Agro, we believe that knowledge sharing is essential for the growth of the agricultural ecosystem. Our blog articles cover a wide range of topics including market dynamics, farming techniques, commodity analysis, and industry trends.
                  </p>
                  
                  <p className="text-[#807979] leading-relaxed">
                    For the complete article and more detailed insights, please visit our official website or contact our team. We're always happy to discuss how these topics can benefit your agricultural business or farming operations.
                  </p>
                </div>
              )}
            </article>

            {/* Share Section */}
            <div className="border-t border-[#EDEDFE] mt-12 pt-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 text-[#807979]">
                  <Share2 className="w-5 h-5" />
                  <span className="font-medium">Share this article:</span>
                </div>
                <div className="flex gap-3">
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#0077B5] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#1877F2] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#1DA1F2] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Back to Blogs */}
            <div className="mt-12">
              <Link
                to="/blogs"
                className="inline-flex items-center gap-2 text-[#004534] font-semibold hover:gap-3 transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to All Blogs
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Blogs */}
      <section className="py-16 bg-[#FAFFEE]">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#004534] mb-8">More Articles</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {blogs.filter(b => b.slug !== slug).slice(0, 3).map((relatedBlog) => (
              <Link
                key={relatedBlog.id}
                to={`/blogs/${relatedBlog.slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group"
              >
                <div className="h-40 bg-gradient-to-br from-[#CACAFC] to-[#EDEDFE] overflow-hidden">
                  {relatedBlog.image ? (
                    <img
                      src={relatedBlog.image}
                      alt={relatedBlog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-[#004534]/30 text-4xl">📰</span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <span className="text-sm text-[#807979]">{relatedBlog.date}</span>
                  <h3 className="text-lg font-semibold text-[#004534] mt-2 line-clamp-2 group-hover:text-[#0C6951] transition-colors">
                    {relatedBlog.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailPage;
